import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { saveToLocalStorage } from '../mock';
import { Car, UserPlus } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    carPlate: '',
    carDetails: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    try {
      const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
      
      // Map frontend fields to backend expected format
      const registrationData = {
        username: formData.email, // Use email as username
        full_name: formData.name,
        password: formData.password,
        phone_number: formData.phone,
        car_plate: formData.carPlate,
        car_model: formData.carDetails
      };
      
      // Call backend API
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();

      if (!response.ok) {
        toast({ 
          title: "Kayıt Başarısız", 
          description: data.detail || "Kayıt sırasında bir hata oluştu.",
          variant: "destructive"
        });
        return;
      }

      // Registration includes auto-login, save user data
      saveToLocalStorage('currentUser', data.user);
      saveToLocalStorage('accessToken', data.access_token);


      if (data.email_sent) {
        toast({ 
          title: "Hoş geldiniz!", 
          description: "Kaydınız oluşturuldu. Doğrulama emaili gönderildi, lütfen email kutunuzu kontrol edin."
        });
      } else {
        toast({ 
          title: "Kayıt Başarılı", 
          description: data.email_error 
            ? `Kaydınız oluşturuldu ancak doğrulama emaili gönderilemedi: ${data.email_error}` 
            : "Kaydınız oluşturuldu.",
          variant: data.email_error ? "destructive" : "default"
        });
      }
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
    } catch (error) {
      console.error('Registration error:', error);
      toast({ 
        title: "Hata", 
        description: "Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-2xl bg-gray-800 border-gray-700">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-brand to-brand p-4 rounded-2xl">
              <Car className="h-12 w-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-white text-2xl text-center font-display tracking-tight">Kayıt Ol</CardTitle>
          <CardDescription className="text-gray-400 text-center">
            Art Garage'a hoş geldiniz! Hesabınızı oluşturun
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Ad Soyad *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white">Telefon *</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="0532 123 4567"
                  className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="carPlate" className="text-white">Araç Plakası *</Label>
                <Input
                  id="carPlate"
                  name="carPlate"
                  value={formData.carPlate}
                  onChange={handleChange}
                  placeholder="34 ABC 123"
                  className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="carDetails" className="text-white">Araç Detayları *</Label>
              <Input
                id="carDetails"
                name="carDetails"
                value={formData.carDetails}
                onChange={handleChange}
                placeholder="2020 BMW 3 Serisi Siyah"
                className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Şifre *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-brand to-brand hover:from-brand hover:to-brand/90 text-white"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Kayıt Ol
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Zaten hesabınız var mı?{' '}
              <Link to="/login" className="text-brand hover:text-brand font-semibold">
                Giriş Yap
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
